import React from 'react';
import { motion } from 'framer-motion';
import { Send, UserCheck, Clock } from 'lucide-react'; // Optional: Lucid icons for visual flair
import { CandidateInvitationStats } from '../Types/CandidateType';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';

interface StatBoxProps {
  label: string;
  value: string | number;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  delay: number;
  onClick?: () => void;
}

const StatBox = ({ label, value, description, icon, gradient, delay, onClick }: StatBoxProps) => (
  <motion.div
    onClick={onClick}  // ✅ added
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03 }}   // ✅ optional but helpful
    whileTap={{ scale: 0.97 }}     // ✅ optional but helpful
    transition={{ duration: 0.4, delay }}
    className={`cursor-pointer relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ${gradient}`} // ✅ cursor added
  >
    {/* Decorative Background Shape */}
    <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
    
    <div className="relative z-10 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="rounded-lg bg-white/20 p-2 backdrop-blur-md">
          {icon}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/80">
          Live Data
        </span>
      </div>
      
      <div>
        <h4 className="text-3xl font-black">{value}</h4>
        <p className="text-sm font-bold opacity-90">{label}</p>
      </div>
      
      <p className="text-[10px] font-medium text-white/70 italic">
        {description}
      </p>
    </div>
  </motion.div>
);

interface Props {
  stats: CandidateInvitationStats;
}

const CandidateInvitationStatsComponent = ({ stats }: Props) => {
  const redirect = useRedirect();

  return (
    <div className="w-full max-w-5xl mx-auto px-2 md:px-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        
        {/* Box 1: Total Sent */}
        <StatBox
          label="Invitations Received"
          value={stats.receivedInvitationCount}
          description="Total outreach attempts this month"
          gradient="bg-gradient-to-br from-blue-600 to-indigo-700"
          delay={0.1}
          icon={<Send className="w-5 h-5" />}
          onClick={() => redirect('/invitations/pending')}
        />

        {/* Box 2: Accepted */}
        <StatBox
          label="Invitations Accepted"
          value={stats.acceptedInvitationCount}
          description="Candidates who expressed interest"
          gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
          delay={0.2}
          icon={<UserCheck className="w-5 h-5" />}
          onClick={() => redirect('/invitations/accepted')}  // ✅ added
        />

        {/* Box 3: Rejected */}
        <StatBox
          label="Invitations Rejected"
          value={stats.rejectedInvitationCount}
          description="Candidates who declined invitations"
          gradient="bg-gradient-to-br from-amber-500 to-orange-600"
          delay={0.3}
          icon={<Clock className="w-5 h-5" />}
          onClick={() => redirect('/invitations/rejected')}  // ✅ added
        />

      </div>
    </div>
  );
};

export default CandidateInvitationStatsComponent;